/**
 * Available Backend Brand Identifiers:
 * - "LifeEssentialsClub" (support@lifeessentials.club)
 * - "TomMillerServices" (contact@tommillerservices.com)
 * - "ByronBayToken" (support@byronbaytoken.com)
 * - "TheConnectionCode" (support@theconnectioncode.com)
 * 
 * Each brand has its own email templates, logos, and sender configurations
 * on the backend. The brandIdentifier must match exactly.
 */

export interface BrandConfig {
  // Core Brand Identity
  siteName: string;                // Main site/brand name displayed in headers and titles
  tagline: string;                 // Short brand slogan or value proposition
  description: string;             // Longer brand description for about sections and meta tags

  // Technical Configuration
  tenantId: string;                // Unique MongoDB tenant ID for data isolation
  apiUrl?: string;                 // Optional override for GraphQL API endpoint
  brandIdentifier: string;         // Backend brand identifier for email templates and notifications

  // Logo and Images
  logo: {
    src: string;                  // Logo image URL or emoji/icon
    alt: string;                  // Alt text for accessibility
    favicon: string;              // Browser favicon path
  };

  // Contact Information
  contact: {
    businessType: string;         // Type of business (e.g., "Business & Blockchain Solutions")
    industry: string;             // Industry category (e.g., "Technology")
    email?: string;               // Primary contact email
    phone?: string;               // Primary contact phone
    address?: string;             // Business physical address
    contactName?: string;         // Primary contact person name
    businessName?: string;        // Legal business name
  };

  // Color Palette
  colors: {
    // Additional accent colors (used throughout the app)
    accentBlue?: string;          // Secondary blue accent color
    primaryBlue?: string;         // Main blue for CTAs and highlights
    primaryBlueHover?: string;    // Hover state for primary blue elements
    starYellow?: string;          // Color for star/favorite icons
    starYellowHover?: string;     // Hover state for star icons
    successGreen?: string;        // Success indicators and checkmarks
    purpleAccent?: string;        // Purple accent for special highlights
    
    // Email module specific colors
    taskCardBg?: string;          // Background for task cards in task lists
    emailPreviewBg?: string;      // Background for email content preview areas
    darkCardBg?: string;          // Dark background for cards in dark theme
    darkBorderSubtle?: string;    // Subtle borders in dark theme
    
    // Highlight colors (for text highlighting and badges)
    highlightYellow?: string;     // Yellow highlight background
    highlightGreen?: string;      // Green highlight background
    highlightBlue?: string;       // Blue highlight background
    highlightPurple?: string;     // Purple highlight background
    highlightRed?: string;        // Red highlight background
    
    badges?: any;                 // Badge color schemes with bg, color, and border
    
    // Primary brand colors
    primary: string;              // Main brand color (buttons, links, primary actions)
    primaryHover: string;         // Hover state for primary color elements
    secondary: string;            // Secondary brand color (secondary actions)
    secondaryHover: string;       // Hover state for secondary color elements

    // Accent colors
    accent: string;               // Accent color for highlights and special elements
    accentLight: string;          // Lighter variant of accent color

    // Background colors
    background: {
      main: string;               // Main page background color
      light: string;              // Light background for sections
      card: string;               // Card/panel background color
      overlay: string;            // Modal/overlay background with transparency
      surface: string;            // Surface color for elevated elements
      darkSurface?: string;       // Dark theme surface color
      cardGradient?: string;      // Gradient background for cards in dark theme
      darkOverlay?: string;       // Dark overlay with transparency
      taskCard?: string;          // Task item background in task lists
      previewBg?: string;         // Email/content preview area background
    };

    // Status colors
    status: {
      success: string;            // Success states, confirmations, completed items
      warning: string;            // Warning states, caution indicators
      error: string;              // Error states, failed operations, deletions
      info: string;               // Informational states, hints, tips
      draft?: string;             // Draft email/document status
      sent?: string;              // Sent email status
      failed?: string;            // Failed email/operation status
      scheduled?: string;         // Scheduled email/task status
      inbound?: string;           // Inbound/received email status
    };

    // Text colors
    text: {
      primary: string;            // Primary text color (headings, important text)
      secondary: string;          // Secondary text color (body text)
      muted: string;              // Muted text (hints, placeholders, less important)
      inverse: string;            // Inverse text (white text on dark backgrounds)
      primaryDark?: string;       // Primary text for dark theme
      secondaryDark?: string;     // Secondary text for dark theme
      mutedDark?: string;         // Muted text for dark theme
    };

    // Border colors
    border: {
      light: string;              // Light borders (subtle separators)
      medium: string;             // Medium borders (standard borders)
      dark: string;               // Dark borders (emphasized borders)
      darkCard?: string;          // Card borders in dark theme
    };
  };

  // Typography
  fonts: {
    heading: string;              // Font family for headings (h1-h6)
    body: string;                 // Font family for body text and UI elements
  };

  // Component styling
  components: {
    button: {
      primaryBg: string;          // Primary button background color
      primaryHover: string;       // Primary button hover background
      secondaryBg: string;        // Secondary button background color
      secondaryHover: string;     // Secondary button hover background
      whiteBg?: string;           // White button background (for dark backgrounds)
      whiteText?: string;         // White button text color
      whiteHover?: string;        // White button hover background
      blueBg?: string;            // Blue button background (email module)
      blueHover?: string;         // Blue button hover background
    };
    tooltip?: {
      bg: string;                 // Tooltip background color
      color: string;              // Tooltip text color
      errorBg?: string;           // Error tooltip background
      darkBg?: string;            // Dark theme tooltip background
    };
    menu?: {
      bg: string;                 // Dropdown menu background
      itemBg: string;             // Menu item background
      itemColor: string;          // Menu item text color
      itemHoverBg: string;        // Menu item hover background
      itemHoverColor: string;     // Menu item hover text color
      borderColor: string;        // Menu border color
    };
    card: {
      bg: string;                 // Card background color
      border: string;             // Card border color
      shadow: string;             // Card shadow (elevation)
      darkShadow?: string;        // Card shadow in dark theme
      backdropFilter?: string;    // Backdrop filter for glass effect
      hoverShadow?: string;       // Card shadow on hover
    };
    navbar: {
      bg: string;                 // Navigation bar background
      text: string;               // Navigation bar text color
      shadow: string;             // Navigation bar shadow
    };
    footer: {
      bg: string;                 // Footer background color
      text: string;               // Footer text color
      linkColor: string;          // Footer link color
      linkHover: string;          // Footer link hover color
    };
    form: {
      bg: string;                 // Form background color
      fieldBg: string;            // Input field background
      fieldBorder: string;        // Input field border
      fieldBorderFocus: string;   // Input field border when focused
      fieldShadow: string;        // Input field shadow
      fieldShadowFocus: string;   // Input field shadow when focused
      labelColor: string;         // Form label text color
      placeholderColor: string;   // Input placeholder text color
    };
  };

  // Business specific
  membership: {
    totalLegacyMemberships: number;  // Total number of legacy members
    memberBenefits: {
      discount: string;               // Member discount description
      features: string[];             // List of member benefits/features
    };
  };
}

// Vanguard Care Configuration
export const brandConfig: BrandConfig = {
  // Core Brand Identity
  siteName: "Vanguard Care",
  tagline: "Flourish together",
  description: "Vanguard Care delivers innovative residential aged care in Queensland. We work collaboratively with residents, their families, and our dedicated team to create supportive and welcoming environments.",

  // Technical Configuration
  tenantId: "6964736ba6709e458084f032",
  brandIdentifier: "VanguardCare",

  // Logo and Images
  logo: {
    src: "/logo/VC_Main Logo_RGB.png",
    alt: "Vanguard Care Logo",
    favicon: "/favicon-v5.ico"
  },

  // Contact Information
  contact: {
    businessType: "Aged Care Provider",
    industry: "Healthcare",
    email: "info@vanguardcare.com.au",
    phone: "+61 7 3000 0000",
    address: "Queensland, Australia",
    contactName: "Vanguard Care",
    businessName: "Vanguard Care Pty Ltd"
  },

  // Color Palette - Vanguard Care (Tech Minimalist Style)
  colors: {
    // Primary brand colors - Orange & Navy Blue
    primary: "#FF6600",        // Primary Orange - main action color
    primaryHover: "#CC4902",   // Darker orange on hover
    secondary: "#03177A",      // Navy Blue - secondary accent
    secondaryHover: "#030C61", // Darker navy blue

    // Accent colors
    accent: "#FF6600",         // Primary Orange
    accentLight: "#FF9D32",    // Lighter Orange

    // Background colors (Tech-focused dark theme)
    background: {
      main: "#000000",         // Black background for tech feel
      light: "#E6EEFF",        // Light blue for light sections
      card: "#03177A",         // Navy Blue for cards
      overlay: "rgba(255, 102, 0, 0.05)", // Orange overlay
      surface: "#EBEBEB",      // Light gray surface
      // Dark theme additions
      darkSurface: "#000000",
      cardGradient: "linear-gradient(135deg, rgba(3, 23, 122, 0.9) 0%, rgba(0, 0, 0, 0.8) 50%, rgba(3, 12, 97, 0.9) 100%)",
      darkOverlay: "rgba(255, 102, 0, 0.1)",
      taskCard: "rgba(3, 23, 122, 0.7)",     // Navy blue for task items
      previewBg: "rgba(3, 12, 97, 0.5)"      // Dark navy for preview
    },

    // Status colors
    status: {
      success: "#30D158",      // Green
      warning: "#FF9D32",      // Light orange
      error: "#FF3B30",        // Red
      info: "#AAC3FF",         // Light blue
      draft: "#6B7280",        // Gray for draft status
      sent: "#22C55E",         // Green for sent
      failed: "#EF4444",       // Red for failed
      scheduled: "#AAC3FF",    // Light blue for scheduled
      inbound: "#FF9D32"       // Orange for inbound
    },

    // Text colors (Tech-focused design)
    text: {
      primary: "#FFFFFF",      // White text on dark backgrounds
      secondary: "#AAC3FF",    // Light Blue for secondary text
      muted: "rgba(255, 255, 255, 0.6)",        // Muted white text
      inverse: "#000000",      // Black text for light backgrounds
      // Dark theme text colors
      primaryDark: "#FFFFFF",
      secondaryDark: "#AAC3FF",
      mutedDark: "rgba(255, 255, 255, 0.5)"
    },

    // Border colors (Tech-focused borders)
    border: {
      light: "rgba(255, 255, 255, 0.1)",   // Light border
      medium: "rgba(170, 195, 255, 0.2)",   // Light blue border
      dark: "rgba(255, 102, 0, 0.3)",       // Orange accent border
      // Dark theme borders
      darkCard: "rgba(255, 102, 0, 0.2)"
    },

    // Additional colors (shared across themes)
    accentBlue: "#AAC3FF",
    primaryBlue: "#03177A",
    primaryBlueHover: "#030C61",
    starYellow: "#FF9D32",
    starYellowHover: "#FF6600",
    successGreen: "#22C55E",
    purpleAccent: "#AAC3FF",

    // Email module specific colors
    taskCardBg: "rgba(3, 23, 122, 0.3)",
    emailPreviewBg: "rgba(3, 12, 97, 0.3)",
    darkCardBg: "rgba(3, 12, 97, 0.9)",
    darkBorderSubtle: "rgba(170, 195, 255, 0.1)",
    highlightYellow: "#FFF8EC",
    highlightGreen: "#D1FAE5",
    highlightBlue: "#E6EEFF",
    highlightPurple: "#E6EEFF",
    highlightRed: "#FEE2E2",

    // Badge colors with transparency
    badges: {
      blue: {
        bg: "rgba(3, 23, 122, 0.2)",
        color: "#AAC3FF",
        border: "rgba(3, 23, 122, 0.3)"
      },
      green: {
        bg: "rgba(34, 197, 94, 0.2)",
        color: "#22C55E",
        border: "rgba(34, 197, 94, 0.3)"
      },
      purple: {
        bg: "rgba(170, 195, 255, 0.2)",
        color: "#AAC3FF",
        border: "rgba(170, 195, 255, 0.3)"
      },
      yellow: {
        bg: "rgba(255, 157, 50, 0.2)",
        color: "#FF9D32",
        border: "rgba(255, 157, 50, 0.3)"
      },
      gray: {
        bg: "rgba(107, 114, 128, 0.2)",
        color: "#6B7280",
        border: "rgba(107, 114, 128, 0.3)"
      },
      orange: {
        bg: "rgba(255, 102, 0, 0.2)",
        color: "#FF6600",
        border: "rgba(255, 102, 0, 0.3)"
      },
      teal: {
        bg: "rgba(170, 195, 255, 0.2)",
        color: "#AAC3FF",
        border: "rgba(170, 195, 255, 0.3)"
      },
      cyan: {
        bg: "rgba(230, 238, 255, 0.2)",
        color: "#AAC3FF",
        border: "rgba(230, 238, 255, 0.3)"
      }
    }
  },

  // Typography (Professional financial services fonts)
  fonts: {
    heading: "Inter, -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', system-ui, sans-serif",
    body: "'Source Sans Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', system-ui, sans-serif"
  },

  // Component styling
  components: {
    button: {
      primaryBg: "#FF6600",
      primaryHover: "#CC4902",
      secondaryBg: "rgba(255, 102, 0, 0.1)",
      secondaryHover: "rgba(255, 102, 0, 0.2)",
      // Light theme button styles for dark backgrounds
      whiteBg: "#FFFFFF",
      whiteText: "#000000",
      whiteHover: "#EBEBEB",
      // Navy blue accent buttons
      blueBg: "#03177A",
      blueHover: "#030C61"
    },
    tooltip: {
      bg: "#03177A",
      color: "#FFFFFF",
      errorBg: "#EF4444",
      darkBg: "#030C61"
    },
    menu: {
      bg: "rgba(3, 12, 97, 0.98)",            // Navy blue dark background
      itemBg: "transparent",                   // Transparent item background
      itemColor: "#E6EEFF",                   // Light blue text
      itemHoverBg: "rgba(255, 102, 0, 0.2)",  // Orange hover background
      itemHoverColor: "#FFFFFF",               // White text on hover
      borderColor: "rgba(170, 195, 255, 0.3)" // Light blue border
    },
    card: {
      bg: "#FFFFFF",
      border: "rgba(3, 23, 122, 0.1)",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
      // Dark theme card styles
      darkShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
      hoverShadow: "0 10px 30px rgba(3, 23, 122, 0.3)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "#000000",
      text: "#FFFFFF",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.3)"
    },
    footer: {
      bg: "#03177A",
      text: "#E6EEFF",
      linkColor: "#FF6600",
      linkHover: "#FF9D32"
    },
    form: {
      bg: "#000000",
      fieldBg: "rgba(3, 23, 122, 0.3)",
      fieldBorder: "rgba(170, 195, 255, 0.2)",
      fieldBorderFocus: "#FF6600",
      fieldShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
      fieldShadowFocus: "0 0 0 3px rgba(255, 102, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.2)",
      labelColor: "#FFFFFF",
      placeholderColor: "rgba(170, 195, 255, 0.6)"
    }
  },

  // Business specific
  membership: {
    totalLegacyMemberships: 0,
    memberBenefits: {
      discount: "Investment advisory services",
      features: [
        "Portfolio Strategy Development",
        "Impact Investment Opportunities",
        "Dedicated Investment Advisor",
        "Quarterly Performance Reviews",
        "Exclusive Investment Access"
      ]
    }
  },
};

// Theme-specific color configurations
export const lightTheme = {
  // Primary brand colors
  primary: "#FF6600",
  primaryHover: "#CC4902",
  secondary: "#03177A",
  secondaryHover: "#030C61",

  // Accent colors
  accent: "#FF6600",
  accentLight: "#FF9D32",

  // Background colors (Light theme)
  background: {
    main: "#FFFFFF",
    light: "#E6EEFF",
    card: "#FFFFFF",
    overlay: "rgba(255, 102, 0, 0.05)",
    surface: "#EBEBEB",
    cardGradient: "linear-gradient(135deg, #FFFFFF 0%, #E6EEFF 100%)",
    darkOverlay: "rgba(3, 23, 122, 0.1)",
    taskCard: "#E6EEFF",
    previewBg: "#E6EEFF"
  },

  // Text colors (Light theme)
  text: {
    primary: "#000000",
    secondary: "#03177A",
    muted: "#030C61",
    inverse: "#FFFFFF",
    primaryDark: "#000000",
    secondaryDark: "#03177A",
    mutedDark: "#030C61"
  },

  // Border colors (Light theme)
  border: {
    light: "rgba(3, 23, 122, 0.1)",
    medium: "rgba(3, 23, 122, 0.2)",
    dark: "rgba(3, 23, 122, 0.3)",
    darkCard: "rgba(255, 102, 0, 0.2)"
  },

  // Component colors (Light theme)
  components: {
    card: {
      bg: "#FFFFFF",
      border: "rgba(3, 23, 122, 0.1)",
      shadow: "0 1px 3px rgba(3, 23, 122, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)",
      darkShadow: "0 1px 3px rgba(3, 23, 122, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)",
      hoverShadow: "0 10px 30px rgba(3, 23, 122, 0.15)",
      backdropFilter: "none"
    },
    navbar: {
      bg: "#FFFFFF",
      text: "#000000",
      shadow: "0 1px 3px rgba(3, 23, 122, 0.1)"
    },
    footer: {
      bg: "#E6EEFF",
      text: "#03177A",
      linkColor: "#FF6600",
      linkHover: "#CC4902"
    },
    button: {
      primaryBg: "#FF6600",
      primaryHover: "#CC4902",
      secondaryBg: "rgba(255, 102, 0, 0.1)",
      secondaryHover: "rgba(255, 102, 0, 0.2)",
      whiteBg: "#03177A",
      whiteText: "#FFFFFF",
      whiteHover: "#030C61",
      blueBg: "#03177A",
      blueHover: "#030C61"
    }
  }
};

export const darkTheme = {
  // Primary brand colors
  primary: "#FF6600",
  primaryHover: "#CC4902",
  secondary: "#03177A",
  secondaryHover: "#030C61",

  // Accent colors
  accent: "#FF6600",
  accentLight: "#FF9D32",

  // Background colors (Dark theme - tech minimalist)
  background: {
    main: "#000000",
    light: "#E6EEFF",
    card: "#03177A",
    overlay: "rgba(255, 102, 0, 0.05)",
    surface: "#EBEBEB",
    darkSurface: "#000000",
    cardGradient: "linear-gradient(135deg, rgba(3, 23, 122, 0.9) 0%, rgba(0, 0, 0, 0.8) 50%, rgba(3, 12, 97, 0.9) 100%)",
    darkOverlay: "rgba(255, 102, 0, 0.1)",
    taskCard: "rgba(3, 23, 122, 0.3)",
    previewBg: "rgba(3, 12, 97, 0.3)"
  },

  // Text colors (Dark theme)
  text: {
    primary: "#000000",
    secondary: "#03177A",
    muted: "#030C61",
    inverse: "#FFFFFF",
    primaryDark: "#FFFFFF",
    secondaryDark: "#AAC3FF",
    mutedDark: "rgba(170, 195, 255, 0.7)"
  },

  // Border colors (Dark theme)
  border: {
    light: "rgba(170, 195, 255, 0.1)",
    medium: "rgba(170, 195, 255, 0.2)",
    dark: "rgba(255, 102, 0, 0.3)",
    darkCard: "rgba(255, 102, 0, 0.2)"
  },

  // Component colors (Dark theme)
  components: {
    card: {
      bg: "#FFFFFF",
      border: "rgba(3, 23, 122, 0.1)",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
      darkShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.5)",
      hoverShadow: "0 10px 30px rgba(255, 102, 0, 0.2)",
      backdropFilter: "blur(10px)"
    },
    navbar: {
      bg: "#000000",
      text: "#FFFFFF",
      shadow: "0 1px 3px rgba(0, 0, 0, 0.3)"
    },
    footer: {
      bg: "#03177A",
      text: "#E6EEFF",
      linkColor: "#FF6600",
      linkHover: "#FF9D32"
    },
    button: {
      primaryBg: "#FF6600",
      primaryHover: "#CC4902",
      secondaryBg: "rgba(255, 102, 0, 0.1)",
      secondaryHover: "rgba(255, 102, 0, 0.2)",
      whiteBg: "#FFFFFF",
      whiteText: "#000000",
      whiteHover: "#EBEBEB",
      blueBg: "#03177A",
      blueHover: "#030C61"
    },
    form: {
      bg: "rgba(0, 0, 0, 0.9)",
      fieldBg: "rgba(3, 23, 122, 0.3)",
      fieldBorder: "rgba(170, 195, 255, 0.2)",
      fieldBorderFocus: "#FF6600",
      fieldShadow: "0 1px 2px rgba(0, 0, 0, 0.3)",
      fieldShadowFocus: "0 0 0 3px rgba(255, 102, 0, 0.2), 0 1px 2px rgba(0, 0, 0, 0.3)",
      labelColor: "#FFFFFF"
    }
  }
};

// Helper function with color mode support
export const getColor = (path: string, colorMode?: 'light' | 'dark'): string => {
  // If no color mode specified, try to get it from localStorage or default to dark
  const mode = colorMode || (localStorage.getItem('chakra-ui-color-mode') as 'light' | 'dark') || 'dark';
  const theme = mode === 'light' ? lightTheme : darkTheme;

  const keys = path.split(".");

  // Special handling for component paths
  if (keys[0] === "components") {
    let value: any = theme;
    for (const key of keys) {
      value = value[key];
      if (value === undefined) {
        // Try to find in brandConfig.colors for backwards compatibility
        let fallback: any = brandConfig.colors;
        const colorKeys = keys.slice(1);
        for (const key of colorKeys) {
          fallback = fallback[key];
          if (fallback === undefined) break;
        }
        if (fallback !== undefined) return fallback;

        console.warn(`Color path "${path}" not found in theme config for mode: ${mode}`);
        return "#03177A"; // Fallback to primary
      }
    }
    return value;
  }

  // Default handling for colors object
  let value: any = theme;
  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      // Try to find in brandConfig.colors for backwards compatibility
      let fallback: any = brandConfig.colors;
      for (const key of keys) {
        fallback = fallback[key];
        if (fallback === undefined) break;
      }
      if (fallback !== undefined) return fallback;

      console.warn(`Color path "${path}" not found in theme config for mode: ${mode}`);
      return "#03177A"; // Fallback to primary
    }
  }

  return value;
};

export const getBrandValue = (path: string): any => {
  const keys = path.split(".");
  let value: any = brandConfig;

  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      console.warn(`Brand config path "${path}" not found`);
      return null;
    }
  }

  return value;
};

export const getComponent = (component: string, property: string, colorMode?: 'light' | 'dark'): string => {
  // If no color mode specified, try to get it from localStorage or default to dark
  const mode = colorMode || (localStorage.getItem('chakra-ui-color-mode') as 'light' | 'dark') || 'dark';
  const theme = mode === 'light' ? lightTheme : darkTheme;

  const componentConfig = (theme.components as any)?.[component];
  if (!componentConfig) {
    // Fall back to brandConfig for backwards compatibility
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const value = fallbackConfig[property];
      if (value) return value;
    }
    console.warn(`Component "${component}" not found in theme config for mode: ${mode}`);
    return "#03177A";
  }

  const value = componentConfig[property];
  if (!value) {
    // Fall back to brandConfig for backwards compatibility
    const fallbackConfig = (brandConfig.components as any)[component];
    if (fallbackConfig) {
      const fallbackValue = fallbackConfig[property];
      if (fallbackValue) return fallbackValue;
    }
    console.warn(`Property "${property}" not found for component "${component}" in mode: ${mode}`);
    return "#03177A";
  }

  return value;
};


// CSS Custom Properties Generator
export const generateCSSCustomProperties = (): string => {
  const { colors } = brandConfig;

  return `
    :root {
      --brand-primary: ${colors.primary};
      --brand-primary-hover: ${colors.primaryHover};
      --brand-secondary: ${colors.secondary};
      --brand-secondary-hover: ${colors.secondaryHover};
      --brand-accent: ${colors.accent};
      --brand-accent-light: ${colors.accentLight};
      
      --brand-bg-main: ${colors.background.main};
      --brand-bg-light: ${colors.background.light};
      --brand-bg-card: ${colors.background.card};
      --brand-bg-overlay: ${colors.background.overlay};
      --brand-bg-surface: ${colors.background.surface};
      
      --brand-success: ${colors.status.success};
      --brand-warning: ${colors.status.warning};
      --brand-error: ${colors.status.error};
      --brand-info: ${colors.status.info};
      
      --brand-text-primary: ${colors.text.primary};
      --brand-text-secondary: ${colors.text.secondary};
      --brand-text-muted: ${colors.text.muted};
      --brand-text-inverse: ${colors.text.inverse};
      
      --brand-border-light: ${colors.border.light};
      --brand-border-medium: ${colors.border.medium};
      --brand-border-dark: ${colors.border.dark};
      
      --brand-font-heading: ${brandConfig.fonts.heading};
      --brand-font-body: ${brandConfig.fonts.body};
    }
  `;
};

export default brandConfig; 